import React, { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { getAPIBaseURL } from "../services/helpers"
import { getData } from "../services/request"

const SORT_LABELS = {
  interview: "Interview answers (default)",
  final: "Final score",
  cv: "CV match",
  confidence: "Confidence",
  english: "English proficiency",
  content: "Answer content"
}

const scoreForSortBy = (item, sortBy = "interview") => {
  const normalized = (sortBy || "interview").toLowerCase()
  switch (normalized) {
    case "cv":
      return item.cvMatchScore ?? item.matchingScore ?? null
    case "interview":
      return item.interviewScore ?? null
    case "confidence":
      return item.confidenceScore ?? null
    case "english":
      return item.englishProficiencyScore ?? null
    case "content":
      return item.contentScore ?? null
    case "final":
    default:
      return item.finalScore ?? item.interviewScore ?? item.matchingScore ?? null
  }
}

const sortLabelForMetric = (sortBy) => SORT_LABELS[sortBy] || sortBy

const ScoreBar = ({ label, value }) => {
  const pct = typeof value === "number" ? Math.max(0, Math.min(100, value)) : null
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] text-gray-600">
        <span>{label}</span>
        <span className="font-semibold text-gray-900">{pct ?? "N/A"}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full bg-emerald-600 transition-all"
          style={{ width: pct != null ? `${pct}%` : "0%" }}
        />
      </div>
    </div>
  )
}

const EmployerJobRankingPage = () => {
  const { jobId } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [payload, setPayload] = useState(null)
  const [sortBy, setSortBy] = useState("interview")

  const load = async (nextSort = sortBy, runNow = true) => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/employme/employer/jobs/${jobId}/ranking`
    const res = await getData(url, {}, { runNow, sortBy: nextSort })
    setLoading(false)
    if (res.error === "" && res.result?.status === 200) {
      setPayload(res.result.data?.data || null)
      return
    }
    setError(res.result?.data?.data || res.result?.data?.message || "Unable to load ranking")
  }

  useEffect(() => {
    load(sortBy, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])

  const handleSortChange = (value) => {
    setSortBy(value)
    load(value, true)
  }

  const handleRefresh = () => {
    load(sortBy, true)
  }

  const ranked = [...(payload?.ranked || [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  )
  const deranked = payload?.deranked || []
  const champion = ranked[0] || null
  const activeSortLabel = sortLabelForMetric(payload?.sortBy || sortBy)

  return (
    <div className="mx-auto max-w-6xl px-3 sm:px-0">
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">Candidate ranking</h2>
              <p className="mt-1 text-xs text-gray-600">
                CV match threshold: <span className="font-semibold">60+</span>. Ranked candidates use the same order as AI rank candidates, sorted by your selected metric.
              </p>
              {payload?.triggeredAt ? (
                <p className="mt-1 text-[11px] text-gray-500">
                  Last refreshed: {new Date(payload.triggeredAt).toLocaleString()}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs font-medium text-gray-700">
                Rank by
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="ml-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs"
                >
                  {(payload?.sortOptions || Object.keys(SORT_LABELS)).map((key) => (
                    <option key={key} value={key}>
                      {SORT_LABELS[key] || key}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={loading}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Refresh ranking
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? <p className="text-sm text-gray-500">Loading ranking...</p> : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          {!loading && !error && (
            <>
              {champion ? (
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold text-gray-900">Top Candidate (Champion)</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    #{champion.position} {champion.candidateName || "Candidate"}
                  </p>
                  <p className="mt-1 text-[11px] text-gray-700">
                    {activeSortLabel}: {scoreForSortBy(champion, payload?.sortBy || sortBy) ?? "N/A"}
                  </p>
                </div>
              ) : null}
            <div className="grid gap-4 xl:grid-cols-2">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Ranked ({ranked.length})
                </h3>
                {!ranked.length ? (
                  <p className="mt-2 text-xs text-gray-500">No ranked candidates yet.</p>
                ) : (
                  <ul className="mt-3 space-y-3">
                    {ranked.map((r) => (
                      <li
                        key={r.applicationId}
                        className="rounded-md border border-gray-200 bg-white p-3 text-xs text-gray-700 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-gray-900">
                            #{r.position} {r.candidateName || "Candidate"}
                          </p>
                          <div className="text-right text-[10px] text-gray-500">
                            <p>{activeSortLabel}: {scoreForSortBy(r, payload?.sortBy || sortBy) ?? "N/A"}</p>
                            <p>Final: {r.finalScore ?? "N/A"}</p>
                          </div>
                        </div>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          <ScoreBar label="Content" value={r.contentScore} />
                          <ScoreBar label="English" value={r.englishProficiencyScore} />
                          <ScoreBar label="Confidence" value={r.confidenceScore} />
                          <ScoreBar label="CV match" value={r.cvMatchScore ?? r.matchingScore} />
                        </div>
                        <Link
                          to={`/employer/jobs/${jobId}/applicants/${r.applicationId}`}
                          className="mt-2 inline-block text-blue-600 hover:underline"
                        >
                          View applicant →
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-lg border border-red-200 bg-red-50/40 p-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Deranked ({deranked.length})
                </h3>
                {!deranked.length ? (
                  <p className="mt-2 text-xs text-gray-500">No deranked candidates.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {deranked.map((d) => (
                      <li
                        key={d.applicationId}
                        className="rounded-md border border-red-200 bg-white p-3 text-xs text-gray-700"
                      >
                        <p className="font-semibold text-gray-900">{d.candidateName || "Candidate"}</p>
                        <p className="mt-1 text-red-700">{d.reason || "Unmatched"}</p>
                        <p className="mt-1 text-[10px] text-gray-500">
                          CV match: {d.cvMatchScore ?? d.matchingScore ?? "N/A"} · Final: {d.finalScore ?? "N/A"}
                        </p>
                        <Link
                          to={`/employer/jobs/${jobId}/applicants/${d.applicationId}`}
                          className="mt-2 inline-block text-blue-600 hover:underline"
                        >
                          View applicant →
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmployerJobRankingPage
